import React, {useState, useEffect} from 'react';
import parse from 'html-react-parser';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartPie, faClock } from "@fortawesome/free-solid-svg-icons";
import {Link} from 'react-router-dom';
import {Helmet} from 'react-helmet';
import { API_CONFIG } from '../config';
import LikeButton from './LikeButton';
import analytics from '../utils/analytics';

function RecipeDetails() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [recipe, setRecipe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [ingredients, setIngredients] = useState([]);
    const [instructions, setInstructions] = useState([]);
    const [isFavorite, setIsFavorite] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [error, setError] = useState(null);
    const page = new URLSearchParams(window.location.search).get('page') || 1;
    const searchValue = localStorage.getItem('lastSearchValue') || '';
    const fromFavorites = new URLSearchParams(window.location.search).get('from') === 'favorites';

    const checkIfFavorite = async () => {
        try {
            const response = await fetch(`${API_CONFIG.BACKEND_URL}/api/user/favorites/check/${id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setIsFavorite(data.isFavorite);
            }
        } catch (error) {
            console.error('Error checking favorite status:', error);
        }
    };

    const toggleFavorite = async () => {
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }

        try {
            setError(null);
            const response = await fetch(`${API_CONFIG.BACKEND_URL}/api/user/favorites/${id}`, {
                method: isFavorite ? 'DELETE' : 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: isFavorite ? undefined : JSON.stringify({
                    title: recipe.title,
                    image: recipe.image
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update favorites');
            }

            setIsFavorite(!isFavorite);
        } catch (error) {
            console.error('Error toggling favorite:', error);
            setError(error.message);
        }
    };

    const callApi = async() => {
        try {
            setLoading(true);
            
            // First try to fetch from user recipes
            const userRecipeResponse = await fetch(`${API_CONFIG.BACKEND_URL}/api/recipes/${id}`);
            
            if (userRecipeResponse.ok) {
                // This is a user recipe
                const userRecipe = await userRecipeResponse.json();
                
                // Transform user recipe to match API format
                const transformedRecipe = {
                    ...userRecipe,
                    readyInMinutes: userRecipe.prepTime + userRecipe.cookTime,
                    summary: userRecipe.description,
                    extendedIngredients: userRecipe.ingredients.map((ingredient, index) => ({
                        id: index,
                        original: `${ingredient.amount} ${ingredient.name}`
                    })),
                    analyzedInstructions: [{
                        steps: userRecipe.instructions.map((instruction, index) => ({
                            number: instruction.step,
                            step: instruction.description
                        }))
                    }],
                    isUserRecipe: true,
                    submittedBy: userRecipe.submittedBy?.username || 'User',
                    submittedByUser: userRecipe.submittedBy
                };
                
                setRecipe(transformedRecipe);
                setIngredients(transformedRecipe.extendedIngredients);
                setInstructions(transformedRecipe.analyzedInstructions[0].steps);
            } else {
                // This is an API recipe
                const url = `${API_CONFIG.API_BASE_URL}/${id}/information?apiKey=${API_CONFIG.APP_KEY}`;
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                
                // Clean up summary HTML and add target="_blank" to all links
                if (data.summary) {
                    data.summary = data.summary.replace(/<a\s/g, '<a target="_blank" rel="noopener noreferrer" ');
                }
                
                // Set recipe data
                setRecipe(data);
                
                // Set ingredients
                setIngredients(data.extendedIngredients || []);
                
                // Set instructions
                if (data.analyzedInstructions && data.analyzedInstructions[0]) {
                    setInstructions(data.analyzedInstructions[0].steps || []);
                } else {
                    setInstructions([{
                        number: 1,
                        step: "Instructions are not available for this recipe."
                    }]);
                }
            }
        } catch (error) {
            console.error('Error fetching recipe details:', error);
            setRecipe(null);
            setIngredients([]);
            setInstructions([{
                number: 1,
                step: "Failed to load recipe details. Please try again later."
            }]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        setIsLoggedIn(!!token);
        if (token) {
            checkIfFavorite();
        }
        callApi();
        window.scrollTo(0, 0);
    }, [id]);

    // Track recipe view when recipe is loaded
    useEffect(() => {
        if (recipe && !loading) {
            analytics.trackRecipeView(recipe.title, recipe.id || recipe._id, recipe.isUserRecipe);
            
            // Update page title in Google Analytics with specific recipe name
            const specificPageTitle = `${recipe.title} | Zorbas' Kitchen`;
            analytics.trackPageView(specificPageTitle, window.location.href);
        }
    }, [recipe, loading]);

    if (loading) {
        return <div className="lds-roller"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>;
    }

    if (!recipe) {
        return (
            <div className="container">
                <div className="row">
                    <div className="col-md-12 text-center">
                        <h3>Recipe not found</h3>
                        <button onClick={() => navigate(-1)} className="btn btn-menu">Back to Results</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="recipe-details-wrapper">
            <Helmet>
                <title>{recipe.title} | Zorbas' Kitchen</title>
                <meta name="description" content={`Learn how to make ${recipe.title}. Authentic Greek recipe with step-by-step instructions, ingredients, and cooking tips.`} />
                <meta name="keywords" content={`${recipe.title}, Greek recipe, Mediterranean food, cooking instructions, ingredients`} />
                
                {/* Open Graph for Recipe */}
                <meta property="og:title" content={`${recipe.title} | Zorbas' Kitchen`} />
                <meta property="og:description" content={`Learn how to make ${recipe.title}. Authentic Greek recipe with step-by-step instructions.`} />
                <meta property="og:type" content="article" />
                <meta property="og:image" content={recipe.image || `${window.location.origin}/img/recipe-placeholder.jpg`} />
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${recipe.title} | Zorbas' Kitchen`} />
                <meta name="twitter:description" content={`Learn how to make ${recipe.title}. Authentic Greek recipe.`} />
                <meta name="twitter:image" content={recipe.image || `${window.location.origin}/img/recipe-placeholder.jpg`} />
                
                {/* Recipe Structured Data */}
                <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Recipe",
                  "name": recipe.title,
                  "description": recipe.summary || recipe.description,
                  "image": recipe.image || `${window.location.origin}/img/recipe-placeholder.jpg`,
                  "author": {
                    "@type": "Person",
                    "name": recipe.isUserRecipe ? (recipe.submittedByUser?.username || "Community Member") : "Traditional Greek Recipe"
                  },
                  "datePublished": recipe.createdAt || new Date().toISOString(),
                  "prepTime": recipe.prepTime ? `PT${recipe.prepTime}M` : undefined,
                  "cookTime": recipe.cookTime ? `PT${recipe.cookTime}M` : undefined,
                  "totalTime": recipe.readyInMinutes ? `PT${recipe.readyInMinutes}M` : undefined,
                  "recipeYield": recipe.servings ? `${recipe.servings} servings` : undefined,
                  "recipeCategory": "Greek Cuisine",
                  "recipeCuisine": "Greek",
                  "recipeIngredient": recipe.extendedIngredients?.map(ing => ing.original) || recipe.ingredients?.map(ing => `${ing.amount} ${ing.name}`) || [],
                  "recipeInstructions": recipe.analyzedInstructions?.[0]?.steps?.map(step => ({
                    "@type": "HowToStep",
                    "position": step.number,
                    "text": step.step
                  })) || recipe.instructions?.map(inst => ({
                    "@type": "HowToStep",
                    "position": inst.step,
                    "text": inst.description
                  })) || [],
                  "nutrition": {
                    "@type": "NutritionInformation"
                  }
                })}
                </script>
            </Helmet>
            <div className="container navigation-item">
                <div className="row">
                    <div className="col-md-12">
                        <nav>
                            <button 
                                onClick={() => fromFavorites ? navigate('/profile') : navigate(`/results/${searchValue}?page=${page}`)} 
                                className="btn btn-menu"
                            >
                                {fromFavorites ? 'Back to Favorites' : 'Back to Results'}
                            </button>
                            <Link to="/"><button className="btn btn-menu" href="#">Home</button></Link>
                        </nav>
                    </div>
                </div>
            </div>
            <div className="container recipe-details">
                <div className="row">
                    <div className="col-md-12 intro-wrapper">
                        <div className="recipe-header">
                            <h3>{recipe.title}</h3>
                            {recipe.isUserRecipe && (
                                <div className="user-recipe-badge-details">
                                    <span className="badge">User Recipe</span>
                                    {recipe.submittedBy && (
                                        <div className="submitted-by">
                                            {recipe.submittedByUser?.profilePicture ? (
                                                <img 
                                                    src={recipe.submittedByUser.profilePicture} 
                                                    alt={recipe.submittedBy} 
                                                    className="submitter-profile-picture-details"
                                                />
                                            ) : (
                                                <span className="submitter-icon-details">👤</span>
                                            )}
                                            <Link to={`/profile/${recipe.submittedBy}`} className="submitter-name-details">
                                                by {recipe.submittedBy}
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <p>
                            <FontAwesomeIcon icon={faChartPie} /> Serving: {recipe.servings} | 
                            <FontAwesomeIcon icon={faClock} /> Ready in {recipe.readyInMinutes} min.
                            <div className="recipe-actions-details">
                                <LikeButton 
                                    recipeId={id} 
                                    initialLikeCount={recipe.likeCount || 0}
                                    recipeTitle={recipe.title}
                                />
                                {isLoggedIn && (
                                    <>
                                        <button 
                                            onClick={toggleFavorite} 
                                            className={`favorite-btn ${isFavorite ? 'favorited' : ''}`}
                                        >
                                            <span className="favorite-icon">
                                                {isFavorite ? '⭐' : '☆'}
                                            </span>
                                            {isFavorite ? ' Remove from Favorites' : ' Add to Favorites'}
                                        </button>
                                        {error && <div className="error-message">{error}</div>}
                                    </>
                                )}
                            </div>
                        </p>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <img className="img-fluid" src={recipe.image} alt={recipe.title}/>
                    </div>
                    <div className="col-md-6 text-center">
                        <p>{parse(recipe.summary)}</p>
                    </div>
                </div>
                <div className="row ingredients-row">
                    <div className="col-md-12">
                        <h4>Ingredients:</h4>
                        <ul className="cook-list">
                            {ingredients.map(ingredient => (
                                <li key={ingredient.id}>
                                    {ingredient.original}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="row instructions-row">
                    <div className="col-md-12">
                        <h4>Instructions:</h4>
                        <ul className="cook-list">
                            {instructions.map(instruction => (
                                <li key={instruction.number}>
                                    {instruction.number}. {instruction.step}
                                </li>
                            ))}
                        </ul>
                        {recipe.sourceUrl && (
                            <p className="source-credit">
                                <small>Recipe source: <a href={recipe.sourceUrl} target="_blank" rel="noopener noreferrer">{recipe.sourceName}</a></small>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RecipeDetails;